import React, { useEffect, useState } from "react";
import TopBar from "../../components/TopBar";
import EmptyState from "../../components/EmptyState";
import { Modal, Field } from "../Fields";
import { api, fmtUSD, fmtNum } from "../../lib/api";
import { useYear } from "../../context/YearContext";
import { Package, Plus, Pencil, Trash2, TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", sku: "", category: "", cost: "", markup_pct: 35, sell_price: "", qty_in_stock: 0, low_stock_threshold: 5, supplier: "", notes: "" };

export default function Parts({ businessId }) {
  const { year } = useYear();
  const [parts, setParts] = useState([]);
  const [profitReport, setProfitReport] = useState({ parts: [], totals: {} });
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [overrideSell, setOverrideSell] = useState(false);

  const load = async () => {
    const [p, r] = await Promise.all([
      api.get(`/parts?business_id=${businessId}${search ? `&q=${encodeURIComponent(search)}` : ""}`),
      api.get(`/parts/profit-report?business_id=${businessId}&year=${year}`),
    ]);
    setParts(p.data);
    setProfitReport(r.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId, year, search]);

  // Derived sell price (live preview in form)
  const previewSell = !overrideSell
    ? ((parseFloat(form.cost) || 0) * (1 + (parseFloat(form.markup_pct) || 0) / 100))
    : parseFloat(form.sell_price) || 0;
  const previewProfit = previewSell - (parseFloat(form.cost) || 0);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      business_id: businessId,
      name: form.name,
      sku: form.sku,
      category: form.category,
      cost: parseFloat(form.cost) || 0,
      markup_pct: parseFloat(form.markup_pct) || 0,
      sell_price: overrideSell ? (parseFloat(form.sell_price) || 0) : null,
      qty_in_stock: parseFloat(form.qty_in_stock) || 0,
      low_stock_threshold: parseFloat(form.low_stock_threshold) || 0,
      supplier: form.supplier,
      notes: form.notes,
    };
    try {
      if (editing) await api.put(`/parts/${editing.id}`, payload);
      else await api.post(`/parts`, payload);
      toast.success(editing ? "Part updated" : "Part added");
      setOpen(false); setEditing(null); setForm(blank); setOverrideSell(false); load();
    } catch { toast.error("Could not save part"); }
  };

  const onEdit = (p) => {
    setEditing(p);
    setForm({
      ...blank, ...p,
      cost: p.cost, markup_pct: p.markup_pct, sell_price: p.sell_price, qty_in_stock: p.qty_in_stock,
    });
    setOverrideSell(false);
    setOpen(true);
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this part?\nNote: invoices that already reference it will keep the snapshot but you won't be able to add it as a line item anymore.")) return;
    await api.delete(`/parts/${id}`); toast.success("Deleted"); load();
  };

  // Merge profit data by id for the table
  const profitById = Object.fromEntries(profitReport.parts.map((p) => [p.id, p]));
  const totals = profitReport.totals || {};
  const lowStockCount = parts.filter((p) => p.qty_in_stock <= (p.low_stock_threshold ?? 5)).length;
  const isLow = (p) => p.qty_in_stock <= (p.low_stock_threshold ?? 5);

  return (
    <div>
      <TopBar
        title="Parts Catalog"
        subtitle="Track your parts inventory with cost + markup. See exactly how much profit each part has earned."
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button data-testid="add-part-btn" onClick={() => { setEditing(null); setForm(blank); setOverrideSell(false); setOpen(true); }} className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#A03B1F] flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Part
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="parts-totals">
        <Stat label="Parts in Catalog" value={parts.length} />
        <Stat
          label="Low Stock Alerts"
          value={lowStockCount}
          tone={lowStockCount > 0 ? "warning" : "muted"}
          hint={lowStockCount > 0 ? "Re-order soon" : "All stocked"}
          testid="parts-low-stock-count"
        />
        <Stat label={`Parts Revenue (${year})`} value={fmtUSD(totals.revenue || 0)} tone="positive" />
        <Stat label="Parts Profit" value={fmtUSD(totals.profit || 0)} tone={totals.profit >= 0 ? "highlight" : "negative"} hint={totals.margin_pct ? `${totals.margin_pct}% margin` : ""} testid="parts-profit" />
      </div>

      <div className="bg-white border border-[#1F1E1B] p-4 mb-4">
        <input data-testid="parts-search" placeholder="Search by name or SKU…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
      </div>

      {parts.length === 0 ? (
        <EmptyState icon={Package} title="No parts in catalog" hint="Add a part and set your markup % to see profit per piece." testid="empty-parts" />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
          <table className="ledger-table" data-testid="parts-table">
            <thead>
              <tr>
                <th>Part</th>
                <th>SKU</th>
                <th className="num">Cost</th>
                <th className="num">Markup</th>
                <th className="num">Sell Price</th>
                <th className="num">Stock</th>
                <th className="num">Sold ({year})</th>
                <th className="num">Profit Earned</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {parts.map((p) => {
                const pr = profitById[p.id] || { qty_sold: 0, profit: 0 };
                return (
                  <tr key={p.id} data-testid={`part-row-${p.id}`}>
                    <td className="font-semibold">{p.name}<div className="text-[10px] uppercase tracking-wider text-[#66645E]">{p.category}</div></td>
                    <td className="tabular text-[#66645E]">{p.sku || "—"}</td>
                    <td className="num">{fmtUSD(p.cost)}</td>
                    <td className="num">{p.markup_pct}%</td>
                    <td className="num font-bold">{fmtUSD(p.sell_price)}</td>
                    <td className="num">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <span>{fmtNum(p.qty_in_stock, 0)}</span>
                        {isLow(p) && (
                          <span
                            data-testid={`part-low-stock-${p.id}`}
                            title={`Below threshold of ${p.low_stock_threshold ?? 5}`}
                            className="inline-flex items-center gap-0.5 bg-[#BC4727] text-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                          >
                            <AlertTriangle className="w-2.5 h-2.5" /> Low
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="num">{fmtNum(pr.qty_sold, 0)}</td>
                    <td className="num font-bold text-[#1A4135]" data-testid={`part-profit-${p.id}`}>{fmtUSD(pr.profit)}</td>
                    <td>
                      <div className="flex gap-1.5 justify-end">
                        <button data-testid={`edit-part-${p.id}`} onClick={() => onEdit(p)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                        <button data-testid={`delete-part-${p.id}`} onClick={() => onDelete(p.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <Modal title={editing ? "Edit Part" : "New Part"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="part-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required><input data-testid="part-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Hydraulic O-ring kit" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="SKU"><input data-testid="part-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category"><input data-testid="part-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Hydraulics / Filters / Belts…" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
              <Field label="Supplier"><input data-testid="part-supplier-input" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="My Cost" required><input data-testid="part-cost-input" required type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" /></Field>
              <Field label="Markup %"><input data-testid="part-markup-input" type="number" step="0.1" value={form.markup_pct} onChange={(e) => setForm({ ...form, markup_pct: e.target.value })} disabled={overrideSell} className={`w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular ${overrideSell ? "opacity-50" : ""}`} /></Field>
              <Field label="Qty in Stock"><input data-testid="part-stock-input" type="number" step="0.01" value={form.qty_in_stock} onChange={(e) => setForm({ ...form, qty_in_stock: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" /></Field>
            </div>
            <Field label="Low-stock alert threshold">
              <input data-testid="part-low-threshold-input" type="number" step="1" min="0" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              <div className="text-[11px] text-[#66645E] mt-1">A red “Low” badge appears when stock drops to this number or below.</div>
            </Field>
            <div className="bg-[#FAF7EC] border border-[#1F1E1B] p-3 flex items-start gap-3">
              <input data-testid="part-override-sell" type="checkbox" checked={overrideSell} onChange={(e) => setOverrideSell(e.target.checked)} className="mt-1" />
              <div className="flex-1">
                <label className="text-xs font-bold uppercase tracking-wider">Override sell price manually</label>
                <div className="text-xs text-[#66645E] mt-0.5">
                  {overrideSell
                    ? "You're setting the sell price directly. Markup % is ignored."
                    : `Sell price will be calculated from your cost + markup.`}
                </div>
                {overrideSell && (
                  <input data-testid="part-sell-price-input" type="number" step="0.01" value={form.sell_price} onChange={(e) => setForm({ ...form, sell_price: e.target.value })} className="mt-2 w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" placeholder="Manual sell price" />
                )}
              </div>
            </div>
            <div className="bg-[#1F1E1B] text-white p-3 grid grid-cols-2 gap-4" data-testid="part-preview">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9E9B90]">Sell Price</div>
                <div className="font-display text-2xl font-black tabular">{fmtUSD(previewSell)}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9E9B90]">Profit / Unit</div>
                <div className="font-display text-2xl font-black tabular text-[#9FD3A1]">{fmtUSD(previewProfit)}</div>
              </div>
            </div>
            <Field label="Notes"><textarea data-testid="part-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" /></Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="part-cancel-btn" onClick={() => setOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="part-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, value, tone, hint, testid }) {
  const color = tone === "positive" ? "text-[#1A4135]" : tone === "warning" ? "text-[#BC4727]" : tone === "negative" ? "text-[#BC4727]" : tone === "muted" ? "text-[#66645E]" : tone === "highlight" ? "text-[#1A4135]" : "text-[#1F1E1B]";
  return (
    <div className="bg-white border border-[#1F1E1B] p-4" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className={`font-display text-3xl font-black tabular mt-1 ${color}`}>{value}</div>
      {hint && <div className="text-[10px] text-[#66645E] mt-1">{hint}</div>}
    </div>
  );
}
